import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Panier local du storefront public — PAR page commerciale.
 *
 * Ne stocke QUE des quantités : tous les prix/totaux affichés viennent du
 * re-quote serveur (POST cart/quote), seule vérité.
 */

export interface CartLine {
  productId: string;
  variantId: string | null;
  portionOptionId?: string | null;
  portionName?: string | null;
  quantity: number;
}

const lineKey = (productId: string, variantId: string | null, portionOptionId: string | null) =>
  `${productId}|${variantId ?? ''}|${portionOptionId ?? ''}`;

interface StorefrontCartState {
  /** pageId → lignes */
  carts: Record<string, Record<string, CartLine>>;
  /** pageId → mode de livraison sélectionné ('pickup' | 'delivery' | 'table') */
  fulfillments: Record<string, 'pickup' | 'delivery' | 'table' | 'digital'>;
  add: (
    pageId: string,
    productId: string,
    variantId: string | null,
    quantity: number,
    portionOptionId?: string | null,
    portionName?: string | null,
  ) => void;
  setQuantity: (
    pageId: string,
    productId: string,
    variantId: string | null,
    quantity: number,
    portionOptionId?: string | null,
  ) => void;
  remove: (
    pageId: string,
    productId: string,
    variantId: string | null,
    portionOptionId?: string | null,
  ) => void;
  clear: (pageId: string) => void;
  setFulfillment: (pageId: string, mode: 'pickup' | 'delivery' | 'table' | 'digital') => void;
}

export const useStorefrontCart = create<StorefrontCartState>()(
  persist(
    (set) => ({
      carts: {},
      fulfillments: {},
      add: (pageId, productId, variantId, quantity, portionOptionId = null, portionName = null) =>
        set((state) => {
          const cart = { ...(state.carts[pageId] ?? {}) };
          const key = lineKey(productId, variantId, portionOptionId);
          const existing = cart[key];
          cart[key] = {
            productId,
            variantId,
            portionOptionId,
            portionName,
            quantity: (existing?.quantity ?? 0) + quantity,
          };
          return { carts: { ...state.carts, [pageId]: cart } };
        }),
      setQuantity: (pageId, productId, variantId, quantity, portionOptionId = null) =>
        set((state) => {
          const key = lineKey(productId, variantId, portionOptionId);
          const previous = state.carts[pageId] ?? {};
          const cart = Object.fromEntries(
            Object.entries(previous).filter(([k]) => k !== key),
          ) as Record<string, CartLine>;
          if (quantity > 0) {
            const existing = previous[key];
            cart[key] = {
              productId,
              variantId,
              portionOptionId,
              portionName: existing?.portionName ?? null,
              quantity,
            };
          }
          return { carts: { ...state.carts, [pageId]: cart } };
        }),
      remove: (pageId, productId, variantId, portionOptionId = null) =>
        set((state) => {
          const key = lineKey(productId, variantId, portionOptionId);
          const cart = Object.fromEntries(
            Object.entries(state.carts[pageId] ?? {}).filter(([k]) => k !== key),
          ) as Record<string, CartLine>;
          return { carts: { ...state.carts, [pageId]: cart } };
        }),
      clear: (pageId) =>
        set((state) => ({
          carts: Object.fromEntries(Object.entries(state.carts).filter(([k]) => k !== pageId)),
        })),
      setFulfillment: (pageId, mode) =>
        set((state) => ({
          fulfillments: { ...state.fulfillments, [pageId]: mode },
        })),
    }),
    { name: 'velko-storefront-cart' },
  ),
);

export const selectCartLines = (
  carts: Record<string, Record<string, CartLine>>,
  pageId: string | undefined,
): CartLine[] => (pageId ? Object.values(carts[pageId] ?? {}) : []);

export const cartCount = (lines: CartLine[]): number =>
  lines.reduce((sum, line) => sum + line.quantity, 0);
